import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AuthContext, {
    SignInCredentials,
    SignUpCredentials,
    PendingUser,
    User,
    SignUpResponse,
} from 'contexts/AuthContext';
import { api } from 'services/api';
import {
    getToken,
    removeSessionStorage,
    createSessionStorage,
} from 'utils/tokenStorage';
import { setAuthorizationHeader } from 'services/interceptors';
import paths from 'router/paths';

export function useAuth() {
    const [user, setUser] = useState<User>();
    const [loadingUserData, setLoadingUserData] = useState(true);
    const [pendingUser, setPendingUser] = useState<PendingUser>();
    const [contactLink, setContactLink] = useState<string>(
        paths.CONTACT_US_PATH
    );
    const navigate = useNavigate();
    const token = getToken();
    const isAuthenticated = Boolean(token);
    const { pathname } = useLocation();

    useEffect(() => {
        const token = getToken();

        if (!token) {
            removeSessionStorage();
            setUser(undefined);
            setContactLink(paths.CONTACT_US_PATH);
            setLoadingUserData(false);
        } else {
            setAuthorizationHeader({ request: api.defaults, token });
            fetchUserData();
        }
    }, [token, pathname, navigate]);

    async function fetchUserData() {
        try {
            const response = await api.post('/auth/userinfo');

            if (response?.data) {
                const {
                    user_email,
                    user_nick,
                    last_update_password,
                    user_lang,
                } = response.data;
                setUser({
                    email: user_email,
                    nickname: user_nick,
                    last_update_password,
                    lang: user_lang,
                    permissions: [],
                    roles: [],
                });
            }
        } catch (error) {
            console.error('Failed to fetch user data:', error);
        } finally {
            setLoadingUserData(false);
        }
    }

    async function signIn({ email, password, captcha }: SignInCredentials) {
        try {
            const response = await api.post('/auth/signin', {
                email,
                password,
                captcha,
            });
            const {
                token,
                refreshToken,
                permissions,
                roles,
                status,
                lang,
                nickname,
                last_update_password,
            } = response.data;

            if (status === 'V') {
                createSessionStorage({ token, refreshToken });
                setUser({
                    email,
                    lang,
                    nickname,
                    last_update_password,
                    permissions: [],
                    roles: [],
                });
                setPendingUser(undefined);
                setAuthorizationHeader({ request: api.defaults, token });
                setContactLink(paths.AFTER_CONTACT_US_PATH);
            } else if (status === 'E') {
                removeSessionStorage();
                setUser(undefined);
                setPendingUser({ email });
                setLoadingUserData(false);
                setContactLink(paths.CONTACT_US_PATH);
            }

            return response.data;
        } catch (error) {
            throw error;
        }
    }

    function signOut() {
        removeSessionStorage();
        setUser(undefined);
        setPendingUser(undefined);
        setLoadingUserData(false);
        setContactLink(paths.CONTACT_US_PATH);
        navigate(paths.ROOT_PATH);
    }

    async function signUp(params: SignUpCredentials) {
        removeSessionStorage();
        setUser(undefined);
        setPendingUser(undefined);
        setLoadingUserData(false);
        setContactLink(paths.CONTACT_US_PATH);

        const response = await api.post('/auth/signup', params);
        const { res_code, res_msg } = response.data;

        if (res_code === 'SUCCESS') {
            setPendingUser({ email: params.email });
        }
        return { res_code, res_msg } as SignUpResponse;
    }

    return {
        isAuthenticated,
        user,
        pendingUser,
        loadingUserData,
        signIn,
        signOut,
        signUp,
        contactLink,
    };
}
